import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { Booking, Stay } from "@/types/domain";
import { EMAIL_PATTERN, FieldError, PhoneField, ResourceDialog, DialogFooter, Req, type ListResponse } from "@/components/resource/dialog-kit";
import { cleanBody, listItems } from "@/components/resource/dialog-utils";
import { DateInput } from "@/components/ui/DateInput";
import { businessDateIso, currency } from "@/lib/format";
import type { RoomTypeOption } from "@/features/settings/RoomTypesSettingsPage";
import type { RatePlan } from "@/features/settings/RatePlansSettingsPage";
import { invalidateInventory, nextDay } from "./types";

type NewBookingForm = {
  // One or more room types with a room count each (several types = a group booking).
  rooms: { roomTypeId: string; count: number }[];
  ratePlanId: string;
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  checkInDate: string;
  checkOutDate: string;
  adults: number;
  children: number;
  discountType: "AMOUNT" | "PERCENT";
  discountValue?: number;
  discountReason: string;
  source: "DIRECT_SITE" | "PHONE" | "WALK_IN" | "ADMIN_ADDED";
  notes: string;
  // Day-use: the room for some hours on the check-in date, no night (no rate plan, no check-out date).
  stayKind: "NIGHT" | "DAY_USE";
  dayUseFrom: string;
  dayUseTo: string;
};

export function NewBookingDialog({ propertyId, date, roomTypeId, onClose }: { propertyId: string; date: string; roomTypeId?: string; onClose: () => void }) {
  // Before the late-arrival cutoff (e.g. a 2 AM walk-in) last night's date is still the working day.
  const property = useQuery({ queryKey: ["property", propertyId], queryFn: () => api<Stay>(`/stays/${propertyId}`) });
  const today = businessDateIso(property.data?.lateArrivalCutoff ?? "06:00");
  const initialCheckInDate = date < today ? today : date;
  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm<NewBookingForm>({
    defaultValues: {
      rooms: [{ roomTypeId: roomTypeId ?? "", count: 1 }],
      ratePlanId: "",
      guestName: "",
      guestPhone: "",
      guestEmail: "",
      checkInDate: initialCheckInDate,
      checkOutDate: nextDay(initialCheckInDate),
      adults: 1,
      children: 0,
      discountType: "AMOUNT",
      discountValue: undefined,
      discountReason: "",
      source: "WALK_IN",
      notes: "",
      stayKind: "NIGHT",
      dayUseFrom: "10:00",
      dayUseTo: "17:00",
    },
  });
  const isDayUse = watch("stayKind") === "DAY_USE";
  // Day-use hours default to the property's.
  useEffect(() => {
    if (!property.data) return;
    setValue("dayUseFrom", property.data.dayUseFrom ?? "10:00");
    setValue("dayUseTo", property.data.dayUseTo ?? "17:00");
  }, [property.data, setValue]);
  const checkInDate = watch("checkInDate") || initialCheckInDate;
  const checkOutDate = watch("checkOutDate") || nextDay(checkInDate);
  const minCheckOutDate = nextDay(checkInDate);

  useEffect(() => {
    if (checkOutDate <= checkInDate) {
      setValue("checkOutDate", minCheckOutDate, { shouldDirty: true, shouldValidate: true });
    }
  }, [checkInDate, checkOutDate, minCheckOutDate, setValue]);

  const roomLines = useFieldArray({ control, name: "rooms" });
  const roomTypes = useQuery({
    queryKey: ["room-types", propertyId],
    queryFn: () => api<ListResponse<RoomTypeOption>>(`/room-types?propertyId=${propertyId}&isActive=true&limit=100`),
  });
  const availableRoomTypes = listItems<RoomTypeOption>(roomTypes.data);
  // Only the plans bookable for these room types and dates: every night within the plan's dates / days, and on
  // festival dates only the festival plan (then booking without a plan isn't allowed).
  const chosenTypes = [...new Set((watch("rooms") ?? []).map((line) => line.roomTypeId).filter(Boolean))];
  const ratePlans = useQuery({
    queryKey: ["rate-plans", propertyId, "for-stay", chosenTypes.join(","), checkInDate, checkOutDate],
    queryFn: () => api<{ plans: RatePlan[]; required: boolean }>(`/rate-plans/for-stay?propertyId=${propertyId}&roomTypeIds=${chosenTypes.join(",")}&checkIn=${checkInDate}&checkOut=${checkOutDate}`),
    enabled: !isDayUse && checkOutDate > checkInDate,
  });
  const availableRatePlans = useMemo(() => ratePlans.data?.plans ?? [], [ratePlans.data]);
  const planRequired = Boolean(ratePlans.data?.required);
  const ratePlanId = watch("ratePlanId");
  // A plan that no longer applies to the new dates / types is cleared; a festival period picks its plan.
  useEffect(() => {
    if (!ratePlans.data) return;
    if (ratePlanId && !availableRatePlans.some((plan) => plan.id === ratePlanId)) setValue("ratePlanId", "");
    if (!ratePlanId && planRequired && availableRatePlans.length === 1) setValue("ratePlanId", availableRatePlans[0].id);
  }, [ratePlans.data, ratePlanId, availableRatePlans, planRequired, setValue]);

  const mutation = useMutation({
    // The price comes from the room type / rate plan; a lower price is a discount with a reason, shown on the bill.
    mutationFn: ({ discountType, discountValue, discountReason, stayKind, dayUseFrom, dayUseTo, ...data }: NewBookingForm) => api<Booking>("/bookings", {
      method: "POST",
      body: cleanBody({
        ...data,
        ...(stayKind === "DAY_USE" ? { checkOutDate: undefined, ratePlanId: undefined, dayUse: { from: dayUseFrom, to: dayUseTo } } : {}),
        stayProfileId: propertyId,
        discount: discountValue && discountValue > 0 ? { type: discountType, value: discountValue, reason: discountReason.trim() } : undefined,
      }),
    }),
    onSuccess: async () => {
      await invalidateInventory(propertyId, date);
      onClose();
    },
  });

  return (
    <ResourceDialog title="New booking" eyebrow="Manual booking" onClose={onClose}>
      <form noValidate onSubmit={handleSubmit((data) => mutation.mutate(data))}>
        <div className="property-form-grid">
          <div className="property-form-grid__wide segmented" role="radiogroup" aria-label="Booking kind">
            <label className={!isDayUse ? "segmented__option is-active" : "segmented__option"}><input type="radio" value="NIGHT" {...register("stayKind")} /> Night stay</label>
            <label className={isDayUse ? "segmented__option is-active" : "segmented__option"}><input type="radio" value="DAY_USE" {...register("stayKind")} /> Day-use (hours)</label>
          </div>
          <div className="property-form-grid__wide room-lines">
            <span className="label-title">Rooms<Req /></span>
            {roomLines.fields.map((field, index) => (
              <div key={field.id} className="room-lines__row">
                <select aria-label="Room type" {...register(`rooms.${index}.roomTypeId` as const, { required: "Select a room type" })}>
                  <option value="">Select a room type</option>
                  {availableRoomTypes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <input type="number" min={1} max={50} aria-label="Number of rooms" {...register(`rooms.${index}.count` as const, { valueAsNumber: true, min: { value: 1, message: "At least 1" } })} />
                {roomLines.fields.length > 1 && <button type="button" className="text-link" onClick={() => roomLines.remove(index)}>Remove</button>}
                <FieldError error={errors.rooms?.[index]?.roomTypeId} />
              </div>
            ))}
            <button type="button" className="text-link" onClick={() => roomLines.append({ roomTypeId: "", count: 1 })}>+ Add another room type (group)</button>
            <small>Rooms are allotted when the booking is confirmed - free, clean rooms kept together are suggested.</small>
          </div>
          {isDayUse ? (
            <div className="form-hint property-form-grid__wide">Day-use is charged {property.data?.dayUsePercent ?? 50}% of the room's nightly price. No night is held - the room can still be sold for tonight.</div>
          ) : (
            <label>{planRequired ? <span className="label-title">Rate plan<Req /></span> : "Rate plan (optional)"}
              <select {...register("ratePlanId", { validate: (value) => isDayUse || !planRequired || Boolean(value) || "Festival / season dates - choose the festival rate" })}>
                {!planRequired && <option value="">No rate plan — plain room price</option>}
                {planRequired && <option value="">Choose the festival rate</option>}
                {availableRatePlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}{plan.isSeasonal ? " (festival)" : ""} — {plan.amountType === "PERCENTAGE" ? `${plan.amountValue}%` : currency.format(Number(plan.amountValue))}</option>)}
              </select>
              <FieldError error={errors.ratePlanId} />
              <small>{planRequired ? "These dates are on the festival / season rate." : "Only plans valid for every night of these dates are listed."}</small>
            </label>
          )}
          <label><span className="label-title">Guest name<Req /></span>
            <input {...register("guestName", { required: "Guest name is required", minLength: { value: 2, message: "Enter at least 2 characters" } })} />
            <FieldError error={errors.guestName} />
          </label>
          <label><span className="label-title">Phone<Req /></span>
            <PhoneField control={control} name="guestPhone" error={errors.guestPhone} required />
          </label>
          <label>Email
            <input type="email" {...register("guestEmail", { validate: (value) => !value || EMAIL_PATTERN.test(value) || "Enter a valid email address" })} />
            <FieldError error={errors.guestEmail} />
          </label>
          <label><span className="label-title">{isDayUse ? "Date" : "Check-in"}<Req /></span>
            <DateInput
              min={today}
              {...register("checkInDate", {
                required: "Check-in date is required",
                validate: (value) => value >= today || "Check-in cannot be before today",
              })}
            />
            <FieldError error={errors.checkInDate} />
          </label>
          {isDayUse ? (
            <label><span className="label-title">Hours<Req /></span>
              <span className="time-range">
                <input type="time" aria-label="From" {...register("dayUseFrom", { required: "From time is required" })} />
                <span>to</span>
                <input type="time" aria-label="To" {...register("dayUseTo", { required: "To time is required", validate: (value, form) => value > form.dayUseFrom || "'To' must be after 'from'" })} />
              </span>
              <FieldError error={errors.dayUseTo} />
            </label>
          ) : (
            <label><span className="label-title">Check-out<Req /></span>
              <DateInput
                min={minCheckOutDate}
                {...register("checkOutDate", {
                  validate: (value) => isDayUse || value >= minCheckOutDate || "Check-out must be after check-in",
                })}
              />
              <FieldError error={errors.checkOutDate} />
            </label>
          )}
          <label><span className="label-title">Adults<Req /></span>
            <input type="number" min={1} {...register("adults", { required: "Adults are required", valueAsNumber: true, min: { value: 1, message: "Must be at least 1" } })} />
            <FieldError error={errors.adults} />
          </label>
          <label>Children
            <input type="number" min={0} {...register("children", { valueAsNumber: true, min: { value: 0, message: "Must be 0 or more" } })} />
            <FieldError error={errors.children} />
          </label>
          <label>Discount (optional)
            <span className="discount-input">
              <select aria-label="Discount type" {...register("discountType")}>
                <option value="AMOUNT">₹</option>
                <option value="PERCENT">%</option>
              </select>
              <input type="number" step="0.01" min={0} placeholder="0" {...register("discountValue", { valueAsNumber: true, min: { value: 0, message: "Must be 0 or more" }, validate: (value, form) => !value || form.discountType !== "PERCENT" || value <= 100 || "At most 100%" })} />
            </span>
            <FieldError error={errors.discountValue} />
          </label>
          {Number(watch("discountValue")) > 0 && (
            <label>Discount reason<Req />
              <input placeholder="e.g. regular guest" {...register("discountReason", { validate: (value, form) => !form.discountValue || value.trim().length >= 3 || "Give a reason for the discount" })} />
              <FieldError error={errors.discountReason} />
            </label>
          )}
          <label>Source
            <select {...register("source")}>
              <option value="WALK_IN">Walk-in</option>
              <option value="PHONE">Phone</option>
              <option value="DIRECT_SITE">Direct site</option>
              <option value="ADMIN_ADDED">Admin added</option>
            </select>
          </label>
          <label className="property-form-grid__wide">Notes<textarea rows={2} {...register("notes")} /></label>
        </div>
        {mutation.error && <div className="form-error">{mutation.error.message}</div>}
        <DialogFooter onClose={onClose} loading={mutation.isPending} label="Create booking" />
      </form>
    </ResourceDialog>
  );
}
